/**
 * View: Merchant Approval
 * Main merchant approval view component
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { usePageTitle } from "@/hooks/usePageTitle";
import { SearchInput } from "@/components/ui/search-input";
import { MerchantApprovalTable } from "./components/MerchantApprovalTable";
import type {
  MerchantApprovalData,
  MerchantApprovalItem,
} from "../../domain/model/MerchantApprovalModel";
import { FetchMerchantApprovalsUseCase } from "../../domain/usecase/MerchantApprovalUseCases";

const PAGE_SIZE = 10;

export const MerchantApprovalView = (): React.JSX.Element => {
  usePageTitle("Merchant Approval");

  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [merchantData, setMerchantData] = useState<MerchantApprovalData>({
    items: [],
    paging: {
      currentPage: 1,
      pageSize: PAGE_SIZE,
      totalRecords: 0,
      totalPages: 1,
      hasNext: false,
      hasPrevious: false,
    },
  });

  const loadMerchantApprovals = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await FetchMerchantApprovalsUseCase({
        page: currentPage,
        pageSize: PAGE_SIZE,
        search: searchQuery || undefined,
      });

      setMerchantData(result);
    } catch (loadError) {
      console.error("Failed to load merchant approvals:", loadError);
      setMerchantData((prev) => ({
        ...prev,
        items: [],
      }));
      setError("Failed to load merchant approvals. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery]);

  useEffect(() => {
    void loadMerchantApprovals();
  }, [loadMerchantApprovals]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleView = (item: MerchantApprovalItem) => {
    navigate(`/swarapoint/merchant-approval/${item.merchantId}`);
  };

  return (
    <MainLayout title="Merchant Approval">
      <div className="flex flex-col gap-4">
        {/* Search Bar */}
        <div className="flex justify-between items-center gap-4">
          <div className="flex-1 max-w-80">
            <SearchInput
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search"
            />
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Merchant Approval Table */}
        <MerchantApprovalTable
          data={merchantData.items}
          paging={merchantData.paging}
          isLoading={isLoading}
          onPageChange={handlePageChange}
          onView={handleView}
        />
      </div>
    </MainLayout>
  );
};

